  // StickiesViewRight.js with added Checklist sticky type
  import React, { useState, useRef, useEffect, useCallback } from 'react';
  import StickyNote from './StickyNote';
  import ChecklistSticky from './ChecklistSticky';  // Import the new component
  import StickiesListPanel from './RightPanel/StickiesListPanel';
  import ZIndexManager from '../services/ZIndexManager';
  import { useTheme } from './contexts/ThemeContext';
  import { useStickies } from './contexts/StickiesContext';
  import { usePanelContext } from './contexts/PanelContext';
  import './StickiesViewRight.css';
  import './StickyNote.css';
  import './ChecklistSticky.css';  // Import checklist CSS
  import { getKeyPointsFromAI } from '../api/aiKeyPointsApi';
  import { useNotes } from './contexts/NotesContext'; // Add this import
  import DraggableFreezeButton from './DraggableFreezeButton';
  import DraggableAIButton from './DraggableAIButton';
  import AICompositionModal from './AICompositionModal';



  // Create a singleton instance - matching StickysViewMain pattern
  const zManagerInstance = new ZIndexManager();

  const StickiesViewRight = ({ selectedNoteId }) => {
    const { stickies, updateSticky, removeSticky, addSticky, saveStickyLayout, loading,  effectiveNoteId,isSubnote } = useStickies();
    const { isRightPanelVisible } = usePanelContext();
    const { theme } = useTheme();
    // Add these state variables to StickiesViewRight.js
    const [showDropMenu, setShowDropMenu] = useState(false);
    const [dropPosition, setDropPosition] = useState({ x: 0, y: 0 });
    const [droppedContent, setDroppedContent] = useState('');
    const [isProcessingDrop, setIsProcessingDrop] = useState(false);
    // Add this state to StickiesViewRight.js
  const [processingIndicators, setProcessingIndicators] = useState([]);
  const { getSelectedNote } = useNotes(); // Add this to access note info
  const [arrangementMode, setArrangementMode] = useState('grid'); // 'grid', 'featured', 'cascade'
  const [isAICompositionModalOpen, setIsAICompositionModalOpen] = useState(false);



    const QUICK_EXTRACTION_TYPES = [
      { key: "keyPoints", title: "Key Points" },
      { key: "detailedInsights", title: "Insights" },
      { key: "quotes", title: "Quotes" }
    ];

    
    const [stickyType, setStickyType] = useState('regular');
    // Use the singleton instance
    const zManagerRef = useRef(zManagerInstance);
    
    // State management for display modes and pins
    const [displayModes, setDisplayModes] = useState(() => {
      return stickies?.reduce((modes, sticky) => ({
        ...modes,
        [sticky.id]: sticky.display_mode || 'both'
      }), {}) || {};
    });

    const [selectedStickies, setSelectedStickies] = useState(() => {
  const savedSelectStates = localStorage.getItem(`selected-stickies-${selectedNoteId}`);
  return savedSelectStates ? JSON.parse(savedSelectStates) : {};
});

    // Create a function to register a sticky with the ZIndexManager
    const registerStickyWithZManager = useCallback((stickyId) => {
      //console.log(`Registering new sticky ${stickyId} with ZIndexManager`);
      const newZIndex = zManagerRef.current.addSticky(stickyId);
      //console.log(`Assigned z-index ${newZIndex} to sticky ${stickyId}`);
      return newZIndex;
    }, []);

    // Add this useEffect
  useEffect(() => {
    if (showDropMenu) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.drop-menu')) {
          setShowDropMenu(false);
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropMenu]);

    // Main effect for note selection change - matching StickysViewMain pattern exactly
    useEffect(() => {
      if (selectedNoteId && stickies && stickies.length > 0) {
      // console.log(`📌 Note changed! Loading stickies for note ${selectedNoteId} in RIGHT PANEL`);

        // Get current stickies in ZIndexManager
        const currentStickies = Array.from(zManagerRef.current._zIndices.keys());
        const newStickyIds = stickies.map(sticky => sticky.id);

        // Remove stickies that belong to the previous note
        const stickiesToRemove = currentStickies.filter(id => !newStickyIds.includes(id));

        if (stickiesToRemove.length > 0) {
          console.log("🧹 Removing old stickies from ZIndexManager:", stickiesToRemove);
          stickiesToRemove.forEach(id => {
            zManagerRef.current._zIndices.delete(id);
          });

          // ✅ Completely replace the ZIndexManager instance!
          console.log("🛑 Fully Resetting ZIndexManager for RIGHT PANEL...");
          zManagerRef.current = new ZIndexManager(); // 🔥 Creates a fresh instance
        }

        // 📌 Add coordinate initialization for stickies without coordinates
        const stickiesWithoutCoords = stickies.filter(
          sticky => sticky.x === null || sticky.y === null
        );

        if (stickiesWithoutCoords.length > 0) {
          const stickyUpdates = stickiesWithoutCoords.map((sticky, index) => {
            const columnWidth = 200; // Smaller for right panel
            const rowHeight = 180;
            const columnsPerRow = Math.floor(400 / columnWidth); // Right panel is narrower
            const row = Math.floor(index / columnsPerRow);
            const col = index % columnsPerRow;

            return {
              id: sticky.id,
              x: col * columnWidth + 40,
              y: row * rowHeight + 40,
              width: sticky.main_width || 150,
              height: sticky.main_height || 150
            };
          });

          stickyUpdates.forEach(update => {
            updateSticky(update.id, update);
          });
        }

        // Update display modes
        setDisplayModes(stickies.reduce((modes, sticky) => ({
          ...modes,
          [sticky.id]: sticky.display_mode || 'both'
        }), {}));

        // ✅ Delay re-initialization to allow React state updates
        setTimeout(() => {
        /// console.log("🚀 Re-initializing stickies for new note in RIGHT PANEL");
          zManagerRef.current.initializeStickies(stickies);
        }, 10);
      }
  }, [selectedNoteId, updateSticky]); // Remove 'stickies' from dependencies


    

    // Save state to localStorage
    useEffect(() => {
  if (selectedNoteId) {
    localStorage.setItem(`selected-stickies-${selectedNoteId}`, JSON.stringify(selectedStickies));
    localStorage.setItem(`sticky-modes-${selectedNoteId}`, JSON.stringify(displayModes));
  }
}, [selectedStickies, displayModes, selectedNoteId]);

    const handleDragOver = (e) => {
      // Required to allow dropping
      e.preventDefault();
      e.stopPropagation();
      
      // Change the cursor to indicate a valid drop target
      e.dataTransfer.dropEffect = 'copy';
    };
    

    const handleDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Get the dropped text
      const droppedText = e.dataTransfer.getData('text/plain') || 
                        e.dataTransfer.getData('text/html') || 
                        '';
      
      if (!droppedText.trim()) return;
    
      // Calculate a valid drop position within the sticky container
      const containerRect = e.currentTarget.getBoundingClientRect();
      const relativeX = Math.max(0, e.clientX - containerRect.left);
      const relativeY = Math.max(0, e.clientY - containerRect.top);
      
      // Save position and content for later use, show menu instead of creating sticky
      setDropPosition({ x: relativeX, y: relativeY });
      setDroppedContent(droppedText);
      setShowDropMenu(true);
      
      // DO NOT create a sticky here - we'll do that from the menu actions
    };

    // Add this function in the StickiesViewRight component
  const renderProcessingIndicators = () => {
    return processingIndicators.map(indicator => (
      <div 
        key={indicator.id}
        className="processing-indicator"
        style={{
          position: 'absolute',
          left: indicator.x,
          top: indicator.y,
          width: '40px',
          height: '40px',
          zIndex: 9999,
          pointerEvents: 'none' // So it doesn't interfere with other interactions
        }}
      >
        <svg viewBox="0 0 100 100" width="100%" height="100%">
          <path
            className="snowflake-path"
            d="M50 15L50 85M15 50L85 50M26 26L74 74M26 74L74 26"
            stroke="#4f46e5"
            strokeWidth="6"
            strokeLinecap="round"
          />
        </svg>
      </div>
    ));
  };

  const handleDropMenuAction = async (action) => {
    // Hide the menu
    setShowDropMenu(false);
    
    // Create a unique ID for this indicator
    const indicatorId = Date.now().toString();
    
    if (action.startsWith('ai_')) {
      // Add processing indicator at drop position
      setProcessingIndicators(prev => [...prev, {
        id: indicatorId,
        x: dropPosition.x,
        y: dropPosition.y
      }]);
      
      // AI extraction processing
      const extractionType = action.replace('ai_', '');
      setIsProcessingDrop(true);
      
      try {
        // Get AI response
        const result = await getKeyPointsFromAI(
          droppedContent, 
          5, // max points
          [extractionType] // Just the selected type
        );
        
        // Check if we have valid data
        const extractedItems = result[extractionType];
        if (extractedItems && extractedItems.length > 0) {
          // Format content
          const typeTitle = QUICK_EXTRACTION_TYPES.find(t => t.key === extractionType)?.title || extractionType;
          let formattedContent = `${typeTitle.toUpperCase()}:\n\n`;
          
          extractedItems.forEach((item, index) => {
            formattedContent += `${index + 1}. ${item}\n\n`;
          });
          
          // Create a sticky with the AI result
          const topZIndex = zManagerRef.current._getTopZIndex() + 10;
          const newStickyId = await addSticky(topZIndex, 'regular');
          
          if (newStickyId) {
            await updateSticky(newStickyId, {
              content: formattedContent.trim(),
              zIndex: topZIndex,
              type: 'regular',
              x: dropPosition.x,
              y: dropPosition.y,
              width: 300, // Bigger for AI content
              height: 350
            });
            console.log('🚀 RIGHT PANEL handleAddSticky completed for sticky:', newStickyId, 'at position:', {
              x: 40 + (existingCount * 30),
              y: 80 + (existingCount * 30)
            });
                        
            handleStickyFocus(newStickyId);
            setDisplayModes(prev => ({...prev, [newStickyId]: 'both'}));
          }
        } else {
          alert('No information could be extracted from the content');
        }
      } catch (err) {
        console.error('Error processing drop with AI:', err);
        alert('Failed to process the text with AI');
      } finally {
        // Remove this specific indicator when done
        setProcessingIndicators(prev => 
          prev.filter(indicator => indicator.id !== indicatorId)
        );
        setIsProcessingDrop(false);
      }
    } else if (action === 'new') {
      // Regular sticky creation code
      const topZIndex = zManagerRef.current._getTopZIndex() + 10;
      const newStickyId = await addSticky(topZIndex, 'regular');
      
      if (newStickyId) {
        await updateSticky(newStickyId, {
          content: droppedContent,
          zIndex: topZIndex,
          type: 'regular',
          x: dropPosition.x,
          y: dropPosition.y,
          width: 200,
          height: 150
        });
        
        handleStickyFocus(newStickyId);
        setDisplayModes(prev => ({...prev, [newStickyId]: 'both'}));
      }
    }
  };

    // Handler functions - matching StickysViewMain pattern
    const handleStickyFocus = useCallback((stickyId) => {
      const sticky = stickies.find(s => s.id === stickyId);
      if (!sticky) return;

      const newZIndex = zManagerRef.current.bringToFront(stickyId);
      if (newZIndex !== null) {
        updateSticky(stickyId, { zIndex: newZIndex });
      }
    }, [stickies, updateSticky]);

    const handleAddSticky = useCallback(async () => {
      // Don't allow adding stickies to subnotes
      if (isSubnote) {
        return;
      }
      
      const topZIndex = zManagerRef.current._getTopZIndex() + 10;
      const newStickyId = await addSticky(topZIndex, stickyType);
      
      if (newStickyId) {
        const newZIndex = zManagerRef.current.addSticky(newStickyId);


    const stickiesWithCoords = stickies.filter(
        sticky => sticky.x !== null && sticky.y !== null &&
                  (!displayModes[sticky.id] || displayModes[sticky.id] !== 'list')
      );
      const existingCount = stickiesWithCoords.length;
        
        await updateSticky(newStickyId, {
          zIndex: newZIndex,
          type: stickyType,
           x: 40 + (existingCount * 30),    // ADD: Right panel X position
           y: 80 + (existingCount * 30),    // ADD: Right panel Y position  
          width: stickyType === 'checklist' ? 250 : 320,   // Just checklist vs regular
          height: stickyType === 'checklist' ? 250 : 250,  // Just checklist vs regular
          title: null,
          content: ''  // No need for AI content check
        });

        handleStickyFocus(newStickyId);
        setDisplayModes(prev => ({...prev, [newStickyId]: 'both'}));  
      }
    }, [stickyType, addSticky, updateSticky, handleStickyFocus, isSubnote, stickies, displayModes]);

    useEffect(() => {
      stickies.forEach((sticky) => {
        // Only register if not already registered
        if (!zManagerRef.current._zIndices.has(sticky.id)) {
          const newZ = zManagerRef.current.addSticky(sticky.id);
          // Optional: force update zIndex if needed
          updateSticky(sticky.id, { zIndex: newZ });
        }
      });
    }, [stickies, updateSticky]);



    const handleSelectChange = useCallback((stickyId, isSelected) => {
  setSelectedStickies(prev => ({
    ...prev,
    [stickyId]: isSelected
  }));
  
  // Save to localStorage
  const newState = { ...selectedStickies, [stickyId]: isSelected };
  localStorage.setItem(`selected-stickies-${selectedNoteId}`, JSON.stringify(newState));
}, [selectedStickies, selectedNoteId]);


    const currentSelectedStickies = {};
stickies?.forEach(sticky => {
  if (selectedStickies[sticky.id]) {
    currentSelectedStickies[sticky.id] = true;
  }
});

    const handleToggleDisplayMode = useCallback((stickyId, newMode) => {
      if (!selectedNoteId) return;

      updateSticky(stickyId, { 
        display_mode: newMode,
        note_id: selectedNoteId
      });

      setDisplayModes(prev => ({
        ...prev,
        [stickyId]: newMode
      }));
    }, [selectedNoteId, updateSticky]);

    const handleExpandFromList = useCallback((stickyId) => {
      handleStickyFocus(stickyId);
    }, [handleStickyFocus]);

    const handleSaveLayout = useCallback((stickyUpdates) => {
      saveStickyLayout(stickyUpdates);
    }, [saveStickyLayout]);

    // Helper function to choose the appropriate sticky component
    const getStickyComponent = (stickyType) => {
      switch(stickyType) {
        case 'checklist':
          return ChecklistSticky;
        default:
          return StickyNote;
      }
    };


    // Create the arrangement cycle function
  const cycleArrangements = () => {
    if (!stickies || stickies.length === 0) return;
    
    console.log("Cycling to next arrangement...");
    
    // Determine next arrangement mode
    let nextMode;
    switch (arrangementMode) {
      case 'grid':
        nextMode = 'featured';
        arrangeStickiesFeatured();
        break;
      case 'featured':
        nextMode = 'cascade';
        arrangeStickiesCascade();
        break;
        case 'cascade':
          default:
            nextMode = 'grid';
            arrangeStickiesInGrid(); // Fixed function name
            break;
    }
    
    setArrangementMode(nextMode);
  };

  // Cascade arrangement function
  const arrangeStickiesCascade = () => {
    if (!stickies || stickies.length === 0) return;
    
    // Configuration
    const startX = 30;
    const startY = 80;
    const xOffset = 40;  // Horizontal offset for each sticky
    const yOffset = 40;  // Vertical offset for each sticky
    const standardWidth = 220;
    const standardHeight = 180;
    
    // Filter visible stickies
    const visibleStickies = stickies.filter(
      sticky => !displayModes[sticky.id] || displayModes[sticky.id] !== 'list'
    );
    
    const updates = visibleStickies.map((sticky, index) => {
      // Calculate cascade position
      const x = startX + (index * xOffset);
      const y = startY + (index * yOffset);
      
      // Adjust dimensions based on sticky type
      let width = standardWidth;
      let height = standardHeight;
      
      if (sticky.type === 'ai') {
        width = 280;
        height = 220;
      } else if (sticky.type === 'checklist') {
        width = 240;
        height = 250;
      }
      
      // Update sticky
      updateSticky(sticky.id, {
        x: x,
        y: y,
        width: width,
        height: height
      });
      
      return {
        id: sticky.id,
        x: x,
        y: y,
        width: width,
        height: height,
        main_x: sticky.main_x,
        main_y: sticky.main_y,
        main_width: sticky.main_width,
        main_height: sticky.main_height,
        display_mode: displayModes[sticky.id] || 'both',
        z_index: sticky.zIndex
      };
    });
    
    // Save to server
    saveStickyLayout(updates);
  };

  const arrangeStickiesFeatured = () => {
    if (!stickies || stickies.length === 0) return;
    
    // Configuration
    const horizontalGap = 30;
    const verticalGap = 30;
    const standardWidth = 220;
    const standardHeight = 180;
    const startX = 30;
    const startY = 80;
    const featuredRowHeight = standardHeight;
    const wideColumnWidth = standardHeight * 4; // Width twice the height for stickies in single column
    
    // Filter visible stickies
    const visibleStickies = stickies.filter(
      sticky => !displayModes[sticky.id] || displayModes[sticky.id] !== 'list'
    );
    
    const updates = visibleStickies.map((sticky, index) => {
      let x, y, width, height;
      
      if (index < 3) {
        // First 3 stickies go in a row
        x = index * (standardWidth + horizontalGap) + startX;
        y = startY;
        width = standardWidth;
        height = featuredRowHeight;
      } else {
        // Remaining stickies go one per row with double width
        x = startX;
        y = startY + featuredRowHeight + verticalGap + (index - 3) * (standardHeight + verticalGap);
        width = wideColumnWidth;
        height = standardHeight;
      }
      
      // Adjust dimensions based on sticky type
      if (sticky.type === 'ai') {
        width = width * 1.2; // 20% wider for AI stickies
        height = height * 1.2; // 20% taller for AI stickies
      } else if (sticky.type === 'checklist') {
        height = height * 1.3; // 30% taller for checklists
      }
      
      // Update sticky
      updateSticky(sticky.id, {
        x: x,
        y: y,
        width: width,
        height: height
      });
      
      return {
        id: sticky.id,
        x: x,
        y: y,
        width: width,
        height: height,
        main_x: sticky.main_x,
        main_y: sticky.main_y,
        main_width: sticky.main_width,
        main_height: sticky.main_height,
        display_mode: displayModes[sticky.id] || 'both',
        z_index: sticky.zIndex
      };
    });
    
    // Save to server
    saveStickyLayout(updates);
  };


    const arrangeStickiesInGrid = () => {
      if (!stickies || stickies.length === 0) return;
      
      console.log("Starting grid arrangement...");
      
      // Configuration for the grid - you can adjust these values
      const itemsPerRow = 3;           // Number of stickies per row
      const horizontalGap = 30;        // Gap between stickies horizontally
      const verticalGap = 30;          // Gap between stickies vertically
      const standardWidth = 220;       // Standard width for all stickies
      const standardHeight = 180;      // Standard height for all stickies
      const startX = 30;               // Starting X position
      const startY = 80;               // Starting Y position (below controls)
      
      // Filter to only visible stickies (not in list mode)
      const visibleStickies = stickies.filter(
        sticky => !displayModes[sticky.id] || displayModes[sticky.id] !== 'list'
      );
      
      console.log(`Arranging ${visibleStickies.length} visible stickies`);
      
      // Create updates for each sticky
      const updates = visibleStickies.map((sticky, index) => {
        const row = Math.floor(index / itemsPerRow);
        const col = index % itemsPerRow;
        
        const x = col * (standardWidth + horizontalGap) + startX;
        const y = row * (standardHeight + verticalGap) + startY;
        
        // Adjust dimensions based on sticky type
        let width = standardWidth;
        let height = standardHeight;
        
        // Set different dimensions based on sticky type
        if (sticky.type === 'ai') {
          width = 280;  // Wider for AI stickies
          height = 220; // Taller for AI stickies
        } else if (sticky.type === 'checklist') {
          width = 240;  // Custom width for checklists
          height = 250; // Taller for checklists
        }
        
        // First update the actual sticky positions directly
        updateSticky(sticky.id, {
          x: x,
          y: y,
          width: width,
          height: height
        });
        
        // Return complete update object for the batch save
        return {
          id: sticky.id,
          x: x,
          y: y,
          width: width,
          height: height,
          main_x: sticky.main_x,
          main_y: sticky.main_y,
          main_width: sticky.main_width,
          main_height: sticky.main_height,
          display_mode: displayModes[sticky.id] || 'both',
          z_index: sticky.zIndex
        };
      });
      
      console.log('Arranging stickies in grid layout:', updates);
      
      // Use the existing function to save updates
    /// saveStickyLayout(updates);
    };

    // Add these functions before the return statement
  const handleFreezeLayout = () => {
    const stickyUpdates = stickies.map(sticky => ({
      id: sticky.id,
      x: sticky.x,
      y: sticky.y,
      width: sticky.width,
      height: sticky.height,
      main_x: sticky.main_x,
      main_y: sticky.main_y,
      main_width: sticky.main_width,
      main_height: sticky.main_height,
      display_mode: displayModes[sticky.id] || 'both',
      z_index: sticky.zIndex
    }));
    
    saveStickyLayout(stickyUpdates);
  };

  const getSelectedStickyIdsArray = () => {
  return Object.entries(currentSelectedStickies)
    .filter(([_, isSelected]) => isSelected)
    .map(([id]) => id);
};

  const handleOpenAIModal = () => {
    setIsAICompositionModalOpen(true);
  };

  const handleSendToAI = (selectedStickies) => {
    console.log('Sending to AI from right panel:', selectedStickies);
    setIsAICompositionModalOpen(false);
  };

  const handleReorderStickies = useCallback((updates) => {
  // Simple implementation - just update each sticky's list_order
  updates.forEach(update => {
    updateSticky(update.id, { list_order: update.list_order });
  });
}, [updateSticky]);
    
    return (
      <div className="sticky-container" data-theme={theme.name} onDragOver={handleDragOver} onDrop={handleDrop}>
  <div className="sticky-controls-wrapper">
    <button className="add-sticky-button" onClick={handleAddSticky}>
      Add Sticky
    </button>
    <button className="add-sticky-button" onClick={cycleArrangements}>
      Arrange: {arrangementMode}
    </button>
  </div>
        
        <div className="stickies-display-area">
          {!loading && stickies?.map(sticky => {
            if (displayModes[sticky.id] === 'list') return null;
            
            const StickyComponent = getStickyComponent(sticky.type);
            
            return (
              <StickyComponent
                key={sticky.id}
                sticky={sticky}
                updateSticky={updateSticky}
                removeSticky={(id) => {
                  zManagerRef.current.removeSticky(id);
                  removeSticky(id);
                }}
                maxZIndex={zManagerRef.current._getTopZIndex()}
                onFocus={() => handleStickyFocus(sticky.id)}
                onToggleDisplayMode={handleToggleDisplayMode}
                isSelected={Boolean(selectedStickies[sticky.id])}
                onSelectChange={handleSelectChange}
                addSticky={addSticky}
                registerWithZManager={registerStickyWithZManager} // Pass the register function
                saveStickyLayout={handleSaveLayout}  // <-- Add this line
              />
            );
          })}
          <DraggableFreezeButton 
    onFreeze={handleFreezeLayout} 
    isRightPanel={true}
    key={`freeze-button-right-${selectedNoteId}`}
  />


  <DraggableAIButton 
    key={`ai-button-right-${selectedNoteId}`} 
    onOpenAIModal={handleOpenAIModal} 
    pinnedCount={getSelectedStickyIdsArray().length}
    isRightPanel={true}
  />
        </div>
        
        <StickiesListPanel
          stickies={stickies || []}
          onExpandSticky={handleExpandFromList}
          displayModes={displayModes}
          onToggleDisplayMode={handleToggleDisplayMode}
          selectedStickies={currentSelectedStickies}  // CHANGED FROM pinnedStickies
          onSelectChange={handleSelectChange}         // NEW PROP
          onSaveLayout={handleSaveLayout}
          onRemoveSticky={removeSticky}
          onReorderSticky={handleReorderStickies} 
          updateSticky={updateSticky}                // NEW PROP
          isRightPanelVisible={isRightPanelVisible}
        />
        {renderProcessingIndicators()}
        {showDropMenu && (
          <div 
            className="drop-menu" 
            style={{ 
              position: 'absolute', 
              left: dropPosition.x, 
              top: dropPosition.y,
              zIndex: 9999,
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              padding: '8px',
              minWidth: '180px'
            }}
          >
            <div className="drop-menu-header" style={{ padding: '4px 12px', fontWeight: 'bold' }}>
              Choose action:
            </div>
            
            {/* Regular option */}
            <button 
              className="drop-menu-button"
              onClick={() => handleDropMenuAction('new')}
              style={{ 
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 12px',
                margin: '4px 0',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                backgroundColor: 'transparent',
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
              onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
              disabled={isProcessingDrop}
            >
              New Sticky
            </button>
            
            {/* Divider */}
            <div style={{ height: '1px', backgroundColor: '#eee', margin: '8px 0' }}></div>
            
            {/* AI extraction options */}
            <div style={{ padding: '4px 12px', fontWeight: 'bold', fontSize: '0.9em', color: '#555' }}>
              AI Extract:
            </div>
            
            {QUICK_EXTRACTION_TYPES.map(type => (
              <button 
                key={type.key}
                className="drop-menu-button"
                onClick={() => handleDropMenuAction(`ai_${type.key}`)}
                style={{ 
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  margin: '4px 0',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isProcessingDrop ? 'not-allowed' : 'pointer',
                  backgroundColor: 'transparent',
                  opacity: isProcessingDrop ? 0.7 : 1
                }}
                onMouseOver={(e) => !isProcessingDrop && (e.target.style.backgroundColor = '#f5f5f5')}
                onMouseOut={(e) => !isProcessingDrop && (e.target.style.backgroundColor = 'transparent')}
                disabled={isProcessingDrop}
              >
                {type.title}
              </button>
            ))}
            
            {isProcessingDrop && (
              <div style={{ padding: '8px 12px', color: '#4f46e5', fontSize: '0.9em' }}>
                Processing with AI...
              </div>
            )}
          </div>
        )}
      

  <AICompositionModal
    isOpen={isAICompositionModalOpen}
    onClose={() => setIsAICompositionModalOpen(false)}
    stickies={stickies || []}
    selectedStickyIds={getSelectedStickyIdsArray()}
    onSendToAI={handleSendToAI}
  />
      </div>
      
    );
    
  };

  export default React.memo(StickiesViewRight);